import { useEffect, useState } from 'react'

import { Button, Grid, Card, CardContent, Typography, useTheme, Divider, TextField, SvgIcon, Box } from "@mui/material";
import { sendEmailLink, signInWithGoogle, useAuth } from "@/firebaseApp";
import { BookingState, selectBooking, useAppSelector } from "@/redux";
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '@/Routes';

export default function Signin() {

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [email, setEmail] = useState('')
  const [showError, setShowError] = useState(false)
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const bookingDetails: BookingState = useAppSelector(selectBooking);


  //TODO ADD call to function sendemaillink : coming from header or coming from booking

  useEffect(() => {
    if (user) {
      navigate(ROUTES.HOME, {
        replace: true
      });
    }
  }, [user]);
  return (


    <Grid container sx={{
      justifyContent: "center",
      alignItems: "center",
    }}>

      <Grid size={{ xs: 10, md: 8, lg: 4 }}>
        <Card
          sx={{
            bgcolor: "background.paper",
            backdropFilter: "blur(12px)",
            boxShadow: "0 25px 50px -12px rgb(0 0 0 / 0.25)",
            mt: 6,
            mb: 6,
            borderRadius: 3,
            marginTop: "60px"
          }}
        >
          <CardContent
            sx={{
              p: { xs: 4, md: 5 },
              display: "flex",
              flexDirection: "column",
              gap: 3,
              textAlign: "center",
            }}
          >
            {/* HEADER */}
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Route Royale
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 1 }}
              >
                Luxury Transportation for Every Occasion
              </Typography>
            </Box>

            {/* SIGN IN TITLE */}
            <Typography
              variant="overline"
              sx={{
                letterSpacing: "0.12em",
                color: "text.disabled",
              }}
            >
              Sign in to continue
            </Typography>

            {/* GOOGLE BUTTON */}
            <Button
              fullWidth
              onClick={async () => {
                const shouldRehydrate = location.state?.rehydrate ?? false;
                const guser = await signInWithGoogle()
                if (shouldRehydrate && guser) navigate(ROUTES.ROUTES_PREVIEW)
              }}
              startIcon={
                <svg width="18" height="18" viewBox="0 0 48 48">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.08 17.74 9.5 24 9.5z" />
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-3.59-13.46-8.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                </svg>
              }
              sx={{
                textTransform: "none",
                borderRadius: 2,
                py: 1.5,
                fontWeight: 500,

                ...(isDark
                  ? {
                    color: "text.primary",
                    border: "1px solid rgba(255,255,255,0.2)",
                    background: "rgba(255,255,255,0.05)",
                  }
                  : {
                    color: "#111",
                    background: "#fff",
                    border: "1px solid #ddd",
                  }),

                "&:hover": {
                  ...(isDark
                    ? { background: "rgba(255,255,255,0.12)" }
                    : { background: "#f5f5f5" }),
                },
              }}
            >
              Continue with Google
            </Button>

            {/* DIVIDER */}
            <Divider sx={{ my: 1 }}>
              <Typography variant="caption" color="text.secondary">
                or sign in with a magic link
              </Typography>
            </Divider>

            {/* EMAIL FIELD */}
            <Box sx={{ textAlign: "left" }}>
              <Typography
                variant="caption"
                sx={{
                  mb: 1,
                  display: "block",
                  color: "text.secondary",
                  letterSpacing: "0.08em",
                }}
              >
                EMAIL ADDRESS
              </Typography>

              <TextField
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                error={showError}
                helperText={showError ? "Enter a valid email address" : ""}

              />
            </Box>

            {/* MAGIC LINK BUTTON */}
            <Button
              fullWidth
              variant="outlined"
              startIcon={
                <SvgIcon>
                  <svg viewBox="0 0 24 24">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                </SvgIcon>
              }
              onClick={() => {
                const isValidEmail = (email: string) =>
                  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

                if (!isValidEmail(email)) {
                  setShowError(true);
                  return;
                }
                const shouldRehydrate = location.state?.rehydrate ?? false;
                sendEmailLink(email, bookingDetails.cacheId as string, shouldRehydrate)
              }}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                py: 1.5,
              }}
            >
              Send magic link
            </Button>

            {/* DESCRIPTION */}
            <Typography variant="body2" color="text.secondary">
              We’ll send a one-time sign-in link to your inbox. No password needed.
            </Typography>

            <Divider />

            {/* TERMS */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.6 }}
            >
              By continuing you agree to Route Royale’s Terms of Service and Privacy
              Policy.
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>

  )
}
